import React from "react";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  DarkMode as DarkModeIcon,
  VolumeUp as VolumeUpIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
  Storage as StorageIcon,
} from "@mui/icons-material";
import { useQuery } from "@apollo/client";
import { GET_MY_AUTH } from "../graphql/queries";
import { ColorModeContext } from "../theme/ThemeProvider";

const SettingsPage = () => {
  const theme = useTheme();
  const colorMode = React.useContext(ColorModeContext);
  const { data: authData } = useQuery(GET_MY_AUTH);

  // State for settings
  const [settings, setSettings] = React.useState({
    darkMode: theme.palette.mode === "dark",
    notifications: true,
    sound: true,
    language: "English",
    dataRetention: 30,
  });

  const handleToggleChange = (setting) => (event) => {
    if (setting === "darkMode") {
      colorMode.toggleColorMode();
    }

    setSettings({
      ...settings,
      [setting]: event.target.checked,
    });
  };

  const handleInputChange = (setting) => (event) => {
    setSettings({
      ...settings,
      [setting]: event.target.value,
    });
  };

  return (
    <Box sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
        Settings
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardHeader title="General Settings" />
            <Divider />
            <CardContent>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <DarkModeIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Dark Mode"
                    secondary="Switch between light and dark theme"
                  />
                  <Switch
                    edge="end"
                    checked={theme.palette.mode === "dark"}
                    onChange={handleToggleChange("darkMode")}
                  />
                </ListItem>

                <Divider variant="inset" component="li" />

                <ListItem>
                  <ListItemIcon>
                    <NotificationsIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Notifications"
                    secondary="Enable/disable all notifications"
                  />
                  <Switch
                    edge="end"
                    checked={settings.notifications}
                    onChange={handleToggleChange("notifications")}
                  />
                </ListItem>

                <Divider variant="inset" component="li" />

                <ListItem>
                  <ListItemIcon>
                    <VolumeUpIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Sound Effects"
                    secondary="Enable/disable sound effects"
                  />
                  <Switch
                    edge="end"
                    checked={settings.sound}
                    onChange={handleToggleChange("sound")}
                  />
                </ListItem>

                <Divider variant="inset" component="li" />

                <ListItem>
                  <ListItemIcon>
                    <LanguageIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Language"
                    secondary="Select your preferred language"
                  />
                  <TextField
                    select
                    SelectProps={{ native: true }}
                    value={settings.language}
                    onChange={handleInputChange("language")}
                    variant="outlined"
                    size="small"
                    sx={{ width: 120 }}
                  >
                    <option value="English">English</option>
                    <option value="Spanish">Spanish</option>
                    <option value="French">French</option>
                  </TextField>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardHeader title="Account Settings" />
            <Divider />
            <CardContent>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Profile Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Display Name"
                      value={authData?.name || ""}
                      variant="outlined"
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Company"
                      value={authData?.company || ""}
                      variant="outlined"
                      disabled
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Change Password
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      type="password"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="New Password"
                      type="password"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type="password"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button variant="contained" color="primary">
                      Update Password
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3}>
            <CardHeader title="System Settings" />
            <Divider />
            <CardContent>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <SecurityIcon color="primary" sx={{ mr: 2 }} />
                    <Typography variant="subtitle1">Security</Typography>
                  </Box>
                  <FormControlLabel
                    control={<Switch checked={true} />}
                    label="Enable Two-Factor Authentication"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <StorageIcon color="primary" sx={{ mr: 2 }} />
                    <Typography variant="subtitle1">Data Management</Typography>
                  </Box>
                  <TextField
                    label="Data Retention (days)"
                    type="number"
                    value={settings.dataRetention}
                    onChange={handleInputChange("dataRetention")}
                    variant="outlined"
                    InputProps={{ inputProps: { min: 1, max: 365 } }}
                    helperText="Number of days to retain scanned data"
                    sx={{ width: 200 }}
                  />
                </Grid>
              </Grid>

              <Box mt={4} display="flex" justifyContent="flex-end">
                <Button variant="contained" color="primary">
                  Save All Settings
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SettingsPage;
